import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Zap, Building2, Shield, Car } from "lucide-react";
import EntityPicker from "@/components/EntityPicker";

const blank_line = () => ({ description: "", code: "", quantity: 1, unit_price: 0, discount_pct: 0 });

function lineAmount(ln) {
  const g = (parseFloat(ln.quantity) || 0) * (parseFloat(ln.unit_price) || 0);
  const d = (parseFloat(ln.discount_pct) || 0) / 100;
  return Math.round(g * (1 - d) * 100) / 100;
}

export default function InvoiceNew() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const initialType = search.get("type") || "body_shop";
  const initialJobId = search.get("job_id") || "";

  const [type, setType] = useState(initialType);
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState(initialJobId);
  const [recipient, setRecipient] = useState({ name: "", email: "", phone: "", address: "" });
  const [vehicle, setVehicle] = useState({ year: "", make: "", model: "", vin: "" });
  const [ro, setRo] = useState("");
  const [issued, setIssued] = useState(new Date().toISOString().slice(0, 10));
  const [due, setDue] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [lines, setLines] = useState([blank_line()]);
  const [taxPct, setTaxPct] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Load completed/in-progress jobs for selector
  useEffect(() => {
    api.get("/jobs").then((r) => {
      const elig = (r.data || []).filter((j) =>
        ["calibration_completed", "report_generated", "invoiced", "in_progress", "scheduled"].includes(j.status)
      );
      setJobs(elig);
    });
  }, []);

  // Auto-fill from job when selected
  const autoFillFromJob = async () => {
    if (!jobId) { toast.error("Pick a job first"); return; }
    try {
      const r = await api.post(`/invoices/generate-from-job/${jobId}?type=${type}`);
      toast.success(`Draft invoice created · ${r.data.invoice_number}`);
      navigate(`/invoices/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-fill failed");
    }
  };

  // Apply selected job's metadata to current form (manual fill)
  const applyJobMetadata = (j) => {
    setJobId(j.id);
    setVehicle({
      year: j.vehicle_year || "",
      make: j.vehicle_make || "",
      model: j.vehicle_model || "",
      vin: j.vin || "",
    });
    setRo(j.claim_number || "");
    if (type === "body_shop") {
      setRecipient({ name: j.body_shop_name || "", email: "", phone: "", address: "" });
    } else {
      setRecipient({ name: j.insurance_company || "", email: "", phone: "", address: "" });
    }
    // Pull estimate lines
    if (j.estimate_lines?.length) {
      setLines(j.estimate_lines.map((el) => ({
        description: el.description || el.operation || "Calibration",
        code: el.operation || "",
        quantity: el.hours || 1,
        unit_price: el.amount || 0,
        discount_pct: 0,
      })));
    }
  };

  const updateLine = (i, key, v) => {
    const next = [...lines];
    next[i][key] = (key === "description" || key === "code") ? v : (parseFloat(v) || 0);
    setLines(next);
  };

  const subtotal = lines.reduce((s, ln) => s + lineAmount(ln), 0);
  const tax = subtotal * (parseFloat(taxPct) / 100 || 0);
  const total = subtotal + tax;

  const submit = async () => {
    if (!recipient.name) return toast.error("Recipient name required");
    if (lines.length === 0 || !lines.some((l) => l.description)) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        type, job_id: jobId || null,
        recipient_name: recipient.name,
        recipient_email: recipient.email || null,
        recipient_phone: recipient.phone || null,
        recipient_address: recipient.address || null,
        ro_number: ro || null,
        issued_date: issued, due_date: due,
        vehicle_year: vehicle.year ? parseInt(vehicle.year) : null,
        vehicle_make: vehicle.make || null,
        vehicle_model: vehicle.model || null,
        vehicle_vin: vehicle.vin || null,
        lines: lines
          .filter((l) => l.description)
          .map((l) => ({
            description: l.description,
            code: l.code || null,
            quantity: parseFloat(l.quantity) || 1,
            unit_price: parseFloat(l.unit_price) || 0,
            discount_pct: parseFloat(l.discount_pct) || 0,
            amount: lineAmount(l),
          })),
        tax_pct: parseFloat(taxPct) || 0,
        notes: notes || null,
      };
      const r = await api.post("/invoices", payload);
      toast.success(`Invoice ${r.data.invoice_number} created`);
      navigate(`/invoices/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up max-w-6xl" data-testid="invoice-new-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Billing Center</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">New Invoice</h1>
          <p className="text-sm text-slate-400 mt-1.5">Generate a body shop or insurance invoice — auto-fill from a completed job or build line-by-line.</p>
        </div>
        {jobId && (
          <Button onClick={autoFillFromJob} variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="auto-generate-btn">
            <Zap className="w-4 h-4 mr-2" /> Auto-Generate Draft from this Job
          </Button>
        )}
      </div>

      {/* Type selector */}
      <div className="flex gap-2">
        {[
          { key: "body_shop", label: "Body Shop Invoice", icon: Building2 },
          { key: "insurance", label: "Insurance Invoice", icon: Shield },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`type-${t.key}`}
              onClick={() => setType(t.key)}
              className={`flex-1 rounded-lg border p-4 text-left transition-colors ${
                type === t.key ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-slate-800 hover:border-slate-700 text-slate-300"
              }`}
            >
              <Icon className={`w-5 h-5 mb-2 ${type === t.key ? "text-blue-300" : "text-slate-500"}`} />
              <div className="font-semibold">{t.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {t.key === "body_shop" ? "Billed to the body shop partner" : "Billed to the insurance carrier (claim)"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Optional job link */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Link to Job (optional)</Label>
        <select
          data-testid="job-select"
          value={jobId}
          onChange={(e) => {
            const v = e.target.value;
            const j = jobs.find((x) => x.id === v);
            if (j) applyJobMetadata(j);
            else { setJobId(""); }
          }}
          className="w-full mt-1.5 bg-[#030712] border border-slate-800 rounded-md h-10 px-3 text-sm"
        >
          <option value="">— No job linked (standalone invoice) —</option>
          {jobs.map((j) => (
            <option key={j.id} value={j.id}>
              {j.vin?.slice(-8)} · {j.vehicle_year} {j.vehicle_make} {j.vehicle_model} · {j.customer_name} · {j.status}
            </option>
          ))}
        </select>
        <div className="text-[10px] text-slate-500 mt-1.5">
          Pick a completed job to auto-fill vehicle, body shop, insurance and estimate lines.
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Bill To */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Bill To</div>
          {type === "body_shop" ? (
            <EntityPicker
              entity="body-shops"
              label="Body Shop"
              testId="bill-to-bs"
              value={recipient.name}
              onPick={(b) => setRecipient({
                name: b.company_name || b.name,
                email: b.email || "",
                phone: b.phone || "",
                address: [b.address, b.city, b.state, b.zip_code].filter(Boolean).join(", "),
              })}
              onClear={() => setRecipient({ name: "", email: "", phone: "", address: "" })}
            />
          ) : (
            <EntityPicker
              entity="insurance"
              label="Insurance Carrier"
              testId="bill-to-ins"
              value={recipient.name}
              onPick={(i) => setRecipient({
                name: i.name,
                email: i.claims_email || "",
                phone: i.phone || "",
                address: "",
              })}
              onClear={() => setRecipient({ name: "", email: "", phone: "", address: "" })}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="recipient-email" className="bg-[#030712] border-slate-800 text-xs" placeholder="Email" value={recipient.email} onChange={(e) => setRecipient({ ...recipient, email: e.target.value })} />
            <Input data-testid="recipient-phone" className="bg-[#030712] border-slate-800 text-xs" placeholder="Phone" value={recipient.phone} onChange={(e) => setRecipient({ ...recipient, phone: e.target.value })} />
          </div>
          <Textarea data-testid="recipient-address" className="bg-[#030712] border-slate-800 text-xs min-h-[60px]" placeholder="Address (single line or multiline)" value={recipient.address} onChange={(e) => setRecipient({ ...recipient, address: e.target.value })} />
        </div>

        {/* Vehicle + metadata */}
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Vehicle & Metadata</div>
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="ro-input" className="bg-[#030712] border-slate-800 font-mono text-sm" placeholder={type === "insurance" ? "Claim #" : "RO #"} value={ro} onChange={(e) => setRo(e.target.value)} />
            <Input data-testid="vin-input" className="bg-[#030712] border-slate-800 font-mono text-sm" placeholder="VIN" value={vehicle.vin} onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase() })} />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input className="bg-[#030712] border-slate-800 text-sm" type="number" placeholder="Year" value={vehicle.year} onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })} />
            <Input className="bg-[#030712] border-slate-800 text-sm" placeholder="Make" value={vehicle.make} onChange={(e) => setVehicle({ ...vehicle, make: e.target.value })} />
            <Input className="bg-[#030712] border-slate-800 text-sm" placeholder="Model" value={vehicle.model} onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Issued</Label>
              <Input data-testid="issued-input" className="mt-1 bg-[#030712] border-slate-800 text-sm" type="date" value={issued} onChange={(e) => setIssued(e.target.value)} />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-bold text-slate-500">Due</Label>
              <Input data-testid="due-input" className="mt-1 bg-[#030712] border-slate-800 text-sm" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Line Items</div>
            <h3 className="font-display text-xl font-medium mt-0.5">ADAS Operations / Services</h3>
          </div>
          <Button size="sm" variant="outline" className="border-slate-700" onClick={() => setLines([...lines, blank_line()])} data-testid="add-line-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Line
          </Button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="text-[9px] uppercase tracking-[0.16em] font-bold text-slate-500 border-b border-slate-800">
              <th className="text-left px-2 py-2">Description</th>
              <th className="text-left px-2 py-2 w-20">Code</th>
              <th className="text-right px-2 py-2 w-20">Price</th>
              <th className="text-right px-2 py-2 w-20">Qty/Hr</th>
              <th className="text-right px-2 py-2 w-16">Dis. %</th>
              <th className="text-right px-2 py-2 w-24">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((ln, i) => (
              <tr key={i} className="border-b border-slate-800/40">
                <td className="px-2 py-1.5">
                  <Input data-testid={`line-desc-${i}`} className="bg-[#030712] border-slate-800 text-sm" value={ln.description} onChange={(e) => updateLine(i, "description", e.target.value)} placeholder="e.g. Front Camera Static Calibration" />
                </td>
                <td className="px-2 py-1.5">
                  <Input className="bg-[#030712] border-slate-800 text-xs font-mono" value={ln.code} onChange={(e) => updateLine(i, "code", e.target.value)} placeholder="CAL" />
                </td>
                <td className="px-2 py-1.5">
                  <Input className="bg-[#030712] border-slate-800 font-mono text-sm text-right" type="number" step="0.01" value={ln.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} />
                </td>
                <td className="px-2 py-1.5">
                  <Input className="bg-[#030712] border-slate-800 font-mono text-sm text-right" type="number" step="0.1" value={ln.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
                </td>
                <td className="px-2 py-1.5">
                  <Input className="bg-[#030712] border-slate-800 font-mono text-sm text-right" type="number" step="1" value={ln.discount_pct} onChange={(e) => updateLine(i, "discount_pct", e.target.value)} />
                </td>
                <td className="px-2 py-1.5 text-right font-mono text-sm font-bold text-emerald-300">${lineAmount(ln).toFixed(2)}</td>
                <td className="px-2">
                  <button onClick={() => setLines(lines.filter((_, x) => x !== i))} className="text-slate-600 hover:text-rose-400" data-testid={`del-line-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <div className="w-72 space-y-1.5">
            <div className="flex justify-between text-sm text-slate-300">
              <span>Subtotal</span><span className="font-mono">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-slate-300 items-center">
              <span className="flex items-center gap-2">
                Tax
                <Input className="w-14 h-7 bg-[#030712] border-slate-800 text-xs font-mono" type="number" step="0.1" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} data-testid="tax-input" />
                <span className="text-xs text-slate-500">%</span>
              </span>
              <span className="font-mono">${tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg pt-2 border-t border-slate-800 font-bold">
              <span className="text-slate-200">TOTAL</span>
              <span className="font-mono text-emerald-300" data-testid="invoice-new-total">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
        <Label className="text-xs uppercase tracking-wide font-bold text-slate-400">Notes (visible on PDF)</Label>
        <Textarea data-testid="notes-input" className="mt-1.5 bg-[#030712] border-slate-800" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, references, anything else…" />
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" className="border-slate-700" onClick={() => navigate("/billing")}>Cancel</Button>
        <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="create-invoice-btn">
          {saving ? "Creating…" : "Create Invoice"}
        </Button>
      </div>
    </div>
  );
}
